import { Card, CardActions, CardContent, CardHeader, FormLabel, Grid, Stack } from "@mui/material";
import type { FC, PropsWithChildren } from "react";
import { useState } from "react";
import type { Control } from "react-hook-form";
import { Controller } from "react-hook-form";
import { AssociatedFilesEdit } from "../components/associatedFiles/AssociatedFilesEdit";
import { AssociatedFilesView } from "../components/associatedFiles/AssociatedFilesView";
import ExpandButton from "../components/entityCard/ExpandButton";
import FormSelect from "../components/formComponents/FormSelect";
import FormTextField, {
  emailValidationPattern,
  phoneNumberValidationPattern,
} from "../components/formComponents/FormTextField";
import GooglePlaceSelect from "../components/placeSelect/GooglePlaceSelect";
import { BankAccounts } from "./BankAccounts";
import type { CustomerFormState } from "./CustomerEdit";
import CustomerLogo from "./CustomerLogo";
import type { AdminCustomerResponse } from "./customersApi";
import { isValidNip } from "./isValidNip";
import { useCustomerFiles } from "./useCustomerFiles";
import { POLISH_VOIVODESHIPS } from "./voivodeships";

export type CustomerWithFiles = Pick<AdminCustomerResponse, "id" | "files" | "logo" | "taxNo" | "bankAccounts">;

const VOIVODESHIP_SELECT_OPTIONS = [
  { value: "", label: "—" },
  ...POLISH_VOIVODESHIPS.map((name) => ({ value: name, label: name })),
];

interface CustomerFilesProps {
  readonly customer: CustomerWithFiles;
  readonly readonly?: boolean;
}

const CustomerFiles: FC<PropsWithChildren<CustomerFilesProps>> = ({ customer, readonly, children }) => {
  const { files, logo, handleAddAttachment, handleRemoveAttachment, handleSetLogo, handleRemoveLogo } =
    useCustomerFiles(customer);
  return (
    <>
      <Grid size={{ xs: 0, lg: 1 }} />
      <Grid size={{ xs: 6, lg: 5 }}>
        <CustomerLogo logo={logo} onSetLogo={handleSetLogo} onDeleteLogo={handleRemoveLogo} />
      </Grid>
      <Grid
        size={{ xs: 6, lg: 6 }}
        sx={{
          alignItems: "end",
          flexDirection: "column",
        }}
      >
        {children}
      </Grid>
      <Grid size={{ xs: 12, lg: 9 }}>
        <Stack direction="column">
          <FormLabel>Załączniki</FormLabel>
          {readonly ? (
            <AssociatedFilesView values={files} />
          ) : (
            <AssociatedFilesEdit
              values={files}
              onNewFile={async (f) => {
                await handleAddAttachment(f);
              }}
              onRemoveFile={async (idx) => {
                await handleRemoveAttachment(idx);
              }}
            />
          )}
        </Stack>
      </Grid>
    </>
  );
};

interface CollapsedProps {
  readonly control: Control<CustomerFormState>;
}

const CollapsedAddressDetails: FC<CollapsedProps> = ({ control }) => {
  return <FormTextField multiline label="Info" control={control} name="info" minRows={3} />;
};

interface ExpandedProps {
  readonly control: Control<CustomerFormState>;
  readonly filesEnabled?: boolean;
  readonly customer?: CustomerWithFiles;
  readonly readonly?: boolean;
}

const ExpandedAddressDetails: FC<ExpandedProps> = ({ control, filesEnabled, customer, readonly }) => {
  return (
    <Grid container size={12} spacing={2}>
      <Grid size={{ xs: 6, lg: 4 }}>
        <FormTextField label="Nazwa" control={control} name="publicContactName" />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <FormTextField label="Adres" control={control} name="publicContactAddress" />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <FormTextField
          label="Telefon"
          type="phone"
          control={control}
          name="publicContactPhone"
          rules={{
            pattern: phoneNumberValidationPattern,
          }}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <FormTextField
          label="Email"
          type="email"
          control={control}
          name="publicContactEmail"
          rules={{
            pattern: emailValidationPattern,
          }}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <FormTextField label="Strona internetowa" type="url" control={control} name="publicContactWebpage" />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <FormTextField
          label="NIP"
          control={control}
          name="nip"
          rules={{ validate: (v) => (!v || isValidNip(v) ? undefined : "Niepoprawny NIP") }}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <FormTextField label="Miasto" control={control} name="publicContactCity" />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <FormSelect
          label="Województwo"
          control={control}
          name="publicContactvoivodeship"
          options={VOIVODESHIP_SELECT_OPTIONS}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <Controller
          name="publicContactGooglePlaceId"
          control={control}
          render={({ field: { onChange, value } }) => <GooglePlaceSelect googlePlaceId={value} onChange={onChange} />}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <FormTextField
          label="Email do faktur"
          type="email"
          control={control}
          name="invoiceEmail"
          rules={{
            pattern: emailValidationPattern,
          }}
        />
      </Grid>
      {filesEnabled && customer ? (
        <CustomerFiles customer={customer} readonly={readonly}>
          <BankAccounts accounts={customer.bankAccounts} />
        </CustomerFiles>
      ) : (
        <Grid
          size={12}
          sx={{
            alignItems: "end",
            flexDirection: "column",
            display: "flex",
          }}
        >
          <BankAccounts accounts={customer?.bankAccounts} />
        </Grid>
      )}
      <Grid size={12}>
        <FormTextField multiline label="Info" control={control} name="info" minRows={3} />
      </Grid>
    </Grid>
  );
};

interface Props {
  readonly control: Control<CustomerFormState>;
  readonly filesEnabled?: boolean;
  readonly customer?: CustomerWithFiles;
  readonly readonly?: boolean;
}

const AddressDetails: FC<Props> = ({ control, filesEnabled, customer, readonly }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card data-testid="address-details">
      <CardHeader subheader="Dane adresowe" />
      <CardContent>
        {expanded ? (
          <ExpandedAddressDetails
            control={control}
            filesEnabled={filesEnabled}
            customer={customer}
            readonly={readonly}
          />
        ) : (
          <CollapsedAddressDetails control={control} />
        )}
      </CardContent>
      <CardActions>
        <ExpandButton expanded={expanded} onChange={setExpanded} />
      </CardActions>
    </Card>
  );
};

export default AddressDetails;
